import React, { Suspense } from 'react';
import { SolutionData, MathSolutionStructuredOutput } from '../types';
import ImageWithModal from './ImageWithModal';
import { useTranslation } from 'react-i18next';

const MathRenderer = React.lazy(() => import('./MathRenderer'));

const SolutionContentSkeleton = () => (
  <div className="prose prose-sm sm:prose-base max-w-none text-slate-700 break-words print:text-black space-y-4 animate-pulse">
    <div>
      <div className="h-5 bg-slate-200 rounded w-1/4 mb-1"></div>
      <div className="h-4 bg-slate-200 rounded w-full"></div>
    </div>
    <div className="pt-4 border-t border-slate-200">
      <div className="h-5 bg-slate-200 rounded w-1/3 mb-1"></div>
      <div className="h-4 bg-slate-200 rounded w-full mt-2"></div>
    </div>
  </div>
);

interface SolutionDisplayProps {
  solution: SolutionData;
}

const SolutionDisplay: React.FC<SolutionDisplayProps> = ({ solution }) => {
  const { t } = useTranslation();
  const solutions = solution.solution_jsonb?.math_solutions;
  if (!solutions || solutions.length === 0) {
    return <p className="text-center text-slate-600 py-10">{t('solutionDisplay.noSolution')}</p>;
  }

  return (
    <div className="space-y-8">
      {/* Top Row: Original Problem */}
      <div>
        <h3 className="text-lg font-semibold text-slate-800 mb-3 print:text-black">{t('solutionDisplay.originalProblem')}</h3>
        <p className="text-sm text-slate-600 mb-1 print:text-black">{t('solutionDisplay.grade')}: {solutions[0].problem_grade}</p>
        <p className="text-sm text-slate-600 mb-3 print:text-black">{t('solutionDisplay.explanationLanguage')}: {solutions[0].problem_language}</p>
        <ImageWithModal
          src={solution.originalImageSrc}
          alt="Math problem"
          className="rounded-md border border-slate-300 object-contain w-full max-h-96 mx-auto md:max-w-md"
        />
      </div>

      {/* Loop through solutions and display each one */}
      {solutions.map((solutionItem: MathSolutionStructuredOutput, index: number) => (
        <div key={index} className="bg-slate-50 p-4 md:p-6 rounded-lg border border-slate-200 print:bg-white print:border-none">
          <h3 className="text-xl font-bold text-slate-800 mb-2 print:text-black">
            {solutionItem.problem_title_generated || (solutions.length > 1 ? t('solutionDisplay.solutionTitle', { index: index + 1 }) : t('solutionDisplay.explanationAndSolution'))}
          </h3>
          <div className="grid grid-cols-2 sm:grid-cols-3 gap-x-4 gap-y-2 mb-6 text-sm text-slate-600 border-b border-slate-200 pb-4">
            {solutionItem.problem_difficulty && <div>
              <span className="font-semibold text-slate-700">{t('solutionDisplay.difficulty')}: </span>
              <span className="capitalize">{solutionItem.problem_difficulty}</span>
            </div>}
            {solutionItem.problem_category && <div>
              <span className="font-semibold text-slate-700">{t('solutionDisplay.category')}: </span>
              <span className="capitalize">{solutionItem.problem_category}</span>
            </div>}
            {solutionItem.confidence_score && <div>
              <span className="font-semibold text-slate-700">{t('solutionDisplay.confidence')}: </span>
              <span>{Math.round(solutionItem.confidence_score * 100)}%</span>
            </div>}
            {solutionItem.key_concepts && solutionItem.key_concepts.length > 0 && <div className="col-span-2 sm:col-span-3">
              <span className="font-semibold text-slate-700">{t('solutionDisplay.keyConcepts')}: </span>
              <span className="capitalize">{solutionItem.key_concepts.join(', ')}</span>
            </div>}
          </div>
          <div className="prose prose-sm sm:prose-base max-w-none text-slate-700 break-words print:text-black space-y-4">
            <Suspense fallback={<SolutionContentSkeleton />}>
              {solutionItem.introduction_latex && (
                <div>
                  <h4 className="font-semibold text-slate-700 mb-1">{t('solutionDisplay.introduction')}:</h4>
                  <MathRenderer latexString={solutionItem.introduction_latex} />
                </div>
              )}

              {solutionItem.steps?.map(step => (
                <div key={step.step_number} className="pt-4 border-t border-slate-200 first:pt-0 first:border-t-0">
                  <h4 className="font-semibold text-slate-700 mb-1">{t('solutionDisplay.step', { stepNumber: step.step_number })}: <MathRenderer latexString={step.description_latex} className="italic text-slate-600 inline" /></h4>
                  <MathRenderer latexString={step.explanation_latex} className="mb-2" />
                  {step.intermediate_result_latex && (
                    <div className="text-sm bg-slate-100 p-2 rounded-md">
                      <span className="font-medium text-slate-500">{t('solutionDisplay.intermediateResult')}: </span>
                      <MathRenderer latexString={step.intermediate_result_latex} className="inline-block" />
                    </div>
                  )}
                </div>
              ))}

              {solutionItem.final_answer_latex && (
                <div className="mt-6">
                  <h4 className="text-lg font-semibold text-slate-800 mb-2">{t('solutionDisplay.finalAnswer')}:</h4>
                  <div className="p-3 bg-sky-50 border border-sky-200 rounded-md">
                    <MathRenderer latexString={solutionItem.final_answer_latex} className="font-bold text-sky-800" />
                  </div>
                </div>
              )}
            </Suspense>
          </div>
        </div>
      ))}
    </div>
  );
};

export default SolutionDisplay;